<!--
  Status spedizioni — Dettagli.
  Redesigned 2026-06-15 (v2.8.0, Phase 1.5 Chunk F.2).

  Drops kendo-dialog + 4× kendo-panelbar/panelbar-item +
  12× kendo-formfield/kendo-label/kendoTextBox + ~40 inline style
  blocks (font-family/color hex/width!important) (82 kendo refs → 0).

  Pattern Diakonix modale XL (mirror borderò-dettagli):
    - Backdrop z-index 2000 (sopra sidebar 1001)
    - Sticky header con titolo + status-pill spedizione + close
    - Body 2 colonne (info ordine + spedizione/corriere), stacked < 900px
    - 3 accordion sotto (Prodotti / Colli grouped / Documenti & Etichette)
    - Footer "Chiudi"

  Bug fixes (vs legacy):
    - Data evasione: legacy aveva assegnazione finale incondizionata
      che azzerava sempre il campo. Ora isClosingEmpty() sentinel handling.
    - linkAllLabels mai popolato: legacy non chiamava mai printAllLabels().
      Ora fetchAllBoxLabels() invocato in load completion.
    - goBack() ora chiama close() (chiude modale) invece di router.navigate.
-->

@if (status_ID) {
  <div class="modal-backdrop" (click)="close()">
    <div
      class="modal modal--xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ship-det-title"
      (click)="$event.stopPropagation()"
    >

      <!-- ─── Header ────────────────────────────────────────────── -->
      <header class="modal__header">
        <h2 id="ship-det-title" class="modal__title">
          <span aria-hidden="true">🚚</span>
          Dettagli spedizione

          @if (order?.or_order_number) {
            <span class="modal__title-tag">n° {{ order.or_order_number }}</span>
          }

          @if (order) {
            <span
              class="status-pill"
              [attr.data-status]="statusState()"
              [title]="order.vs_details?.tab_order_status_desc || ''"
            >
              @switch (statusState()) {
                @case ('pending')    { ⏱ In attesa }
                @case ('shipped')    { 📦 Spedito }
                @case ('in-transit') { 🚚 In transito }
                @case ('delivered')  { ✓ Consegnato }
                @default             { — }
              }
            </span>
          }
        </h2>

        <button
          type="button"
          class="modal-close"
          (click)="close()"
          aria-label="Chiudi"
        >×</button>
      </header>

      <!-- ─── Body ──────────────────────────────────────────────── -->
      <div class="modal__body">

        @if (loading) {
          <div class="loading-card" aria-live="polite">
            <div class="loading-card__spinner" aria-hidden="true"></div>
            <p>Caricamento dettagli spedizione…</p>
          </div>
        } @else if (order) {

          <!-- ─── Info top — 2 columns ───────────────────────────── -->
          <div class="info-cols">

            <!-- Col 1 — Ordine + cliente -->
            <section class="info-card">
              <header class="info-card__header">
                <h3 class="info-card__title">📋 Ordine &amp; cliente</h3>
              </header>

              <div class="info-grid">
                <div class="info-field">
                  <span class="info-field__label">N° Ordine</span>
                  <span class="info-field__value info-field__value--strong">
                    {{ order.or_order_number || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Data ordine</span>
                  <span class="info-field__value">{{ formattedDate }}</span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Data evasione</span>
                  <span class="info-field__value">{{ formattedClosedDate }}</span>
                </div>

                <div class="info-field info-field--full">
                  <span class="info-field__label">Cliente</span>
                  <span class="info-field__value">{{ order.or_customer || '—' }}</span>
                </div>

                <div class="info-field info-field--full">
                  <span class="info-field__label">Indirizzo di spedizione</span>
                  <span class="info-field__value">
                    {{ order.or_shipping_address || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Città</span>
                  <span class="info-field__value">
                    {{ order.or_shipping_city || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">CAP</span>
                  <span class="info-field__value">
                    {{ order.or_shipping_zip || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Provincia</span>
                  <span class="info-field__value">
                    {{ order.or_shipping_prov || '—' }}
                  </span>
                </div>

                <div class="info-field info-field--full">
                  <span class="info-field__label">Nazione</span>
                  <span class="info-field__value">
                    {{ order.or_shipping_country || '—' }}
                  </span>
                </div>

                @if (order.or_note) {
                  <div class="info-field info-field--full">
                    <span class="info-field__label">Riferimento / Note</span>
                    <span class="info-field__value">{{ order.or_note }}</span>
                  </div>
                }
              </div>
            </section>

            <!-- Col 2 — Spedizione + corriere -->
            <section class="info-card">
              <header class="info-card__header">
                <h3 class="info-card__title">🚚 Spedizione</h3>
              </header>

              <div class="info-grid info-grid--two">
                <div class="info-field info-field--full">
                  <span class="info-field__label">Stato</span>
                  <span class="info-field__value info-field__value--strong">
                    {{ order.vs_details?.tab_order_status_desc || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Corriere</span>
                  <span class="info-field__value">
                    {{ order.vs_details?.carrier_desc || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Priorità</span>
                  <span class="info-field__value">
                    {{ order.vs_details?.tab_order_priority_desc || '—' }}
                  </span>
                </div>

                @if (order.shipment) {
                  @if (order.shipment.os_tracking_number) {
                    <div class="info-field info-field--full">
                      <span class="info-field__label">Numero tracking</span>
                      <span class="info-field__value info-field__value--mono">
                        {{ order.shipment.os_tracking_number }}
                      </span>
                    </div>
                  }
                }

                <div class="info-field">
                  <span class="info-field__label">N° colli</span>
                  <span class="info-field__value info-field__value--strong">
                    {{ order.boxes?.length ?? 0 }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">N° prodotti</span>
                  <span class="info-field__value info-field__value--strong">
                    {{ order.products?.length ?? 0 }}
                  </span>
                </div>
              </div>
            </section>
          </div>

          <!-- ─── Accordions ─────────────────────────────────────── -->
          <div class="accordion">

            <!-- Prodotti -->
            <section class="accordion__item">
              <button
                type="button"
                class="accordion__header"
                [class.accordion__header--open]="openSection === 'products'"
                (click)="toggleSection('products')"
                [attr.aria-expanded]="openSection === 'products'"
              >
                <span class="accordion__icon" aria-hidden="true">📦</span>
                <span class="accordion__title">Prodotti</span>
                <span class="accordion__count">{{ order.products?.length ?? 0 }}</span>
                <span class="accordion__chevron" aria-hidden="true">
                  @if (openSection === 'products') { ▾ } @else { ▸ }
                </span>
              </button>

              @if (openSection === 'products') {
                <div class="accordion__body accordion__body--flush">
                  <!--
                    🚨 v2.12.0 L.3: reuse di <app-product-details-storico>
                    (sub-modale H.5 di storico-ordini-dettagli). Mostra
                    lista prodotti con sub-prodotti expandable + click su
                    un prodotto → modal QR codes (pattern già rodato dal
                    chunk H.5 v2.9.0). Sostituisce la line-list shallow
                    precedente che non aveva QR né drill-down.
                  -->
                  @if (order.products && order.products.length > 0) {
                    <app-product-details-storico
                      [order]="order"
                      [opened]="true"
                    ></app-product-details-storico>
                  } @else {
                    <p class="accordion__empty">Nessun prodotto registrato.</p>
                  }
                </div>
              }
            </section>

            <!-- Colli -->
            <section class="accordion__item">
              <button
                type="button"
                class="accordion__header"
                [class.accordion__header--open]="openSection === 'boxes'"
                (click)="toggleSection('boxes')"
                [attr.aria-expanded]="openSection === 'boxes'"
              >
                <span class="accordion__icon" aria-hidden="true">🗳️</span>
                <span class="accordion__title">Colli</span>
                <span class="accordion__count">{{ order.boxes?.length ?? 0 }}</span>
                <span class="accordion__chevron" aria-hidden="true">
                  @if (openSection === 'boxes') { ▾ } @else { ▸ }
                </span>
              </button>

              @if (openSection === 'boxes') {
                <div class="accordion__body">
                  @if (groupedBoxes.length > 0) {
                    <ul class="line-list" role="list">
                      @for (g of groupedBoxes; track g.ts_box_type_desc) {
                        <li class="line-row">
                          <span class="line-row__desc">
                            {{ g.ts_box_type_desc || '—' }}
                          </span>
                          <span class="line-row__qty">
                            ×{{ g.quantity }}
                          </span>
                        </li>
                      }
                    </ul>
                  } @else {
                    <p class="accordion__empty">Nessun collo registrato.</p>
                  }
                </div>
              }
            </section>

            <!-- Documenti + Etichette unificati -->
            <section class="accordion__item">
              <button
                type="button"
                class="accordion__header"
                [class.accordion__header--open]="openSection === 'docs'"
                (click)="toggleSection('docs')"
                [attr.aria-expanded]="openSection === 'docs'"
              >
                <span class="accordion__icon" aria-hidden="true">📎</span>
                <span class="accordion__title">Documenti &amp; Etichette</span>
                <span class="accordion__count">
                  {{
                    (documents?.length ?? 0) +
                    (linkAllLabels ? 1 : 0) +
                    (isLinkToDDT ? 1 : 0) +
                    (linkCarrier ? 1 : 0)
                  }}
                </span>
                <span class="accordion__chevron" aria-hidden="true">
                  @if (openSection === 'docs') { ▾ } @else { ▸ }
                </span>
              </button>

              @if (openSection === 'docs') {
                <div class="accordion__body">

                  <!-- Documenti generici -->
                  @if (documents && documents.length > 0) {
                    <h4 class="accordion__subtitle">Documenti</h4>
                    <ul class="line-list" role="list">
                      @for (doc of documents; track doc.document_id) {
                        <li class="line-row">
                          <span class="line-row__desc">{{ doc.td_document }}</span>
                          @if (doc.td_file) {
                            <a
                              class="file-link"
                              [href]="doc.td_file"
                              target="_blank"
                              rel="noopener"
                              title="Apri documento"
                            >📄 Apri</a>
                          }
                        </li>
                      }
                    </ul>
                  }

                  <!-- Etichette PDF -->
                  <h4 class="accordion__subtitle">Etichette</h4>
                  @if (linkAllLabels || isLinkToDDT || linkCarrier) {
                    <div class="label-grid">
                      @if (linkAllLabels) {
                        <a
                          class="label-card"
                          [href]="linkAllLabels"
                          target="_blank"
                          rel="noopener"
                          title="Apri etichette Scatola (PDF)"
                        >
                          <span class="label-card__icon" aria-hidden="true">📦</span>
                          <span class="label-card__title">Scatole</span>
                          <span class="label-card__sub">PDF</span>
                        </a>
                      }

                      @if (isLinkToDDT && linkToDDT) {
                        <a
                          class="label-card"
                          [href]="linkToDDT"
                          target="_blank"
                          rel="noopener"
                          title="Apri DDT (PDF)"
                        >
                          <span class="label-card__icon" aria-hidden="true">🧾</span>
                          <span class="label-card__title">DDT</span>
                          <span class="label-card__sub">PDF</span>
                        </a>
                      }

                      @if (linkCarrier) {
                        <a
                          class="label-card"
                          [href]="linkCarrier"
                          target="_blank"
                          rel="noopener"
                          title="Apri etichetta corriere (PDF)"
                        >
                          <span class="label-card__icon" aria-hidden="true">🚚</span>
                          <span class="label-card__title">Corriere</span>
                          <span class="label-card__sub">PDF</span>
                        </a>
                      }
                    </div>
                  } @else {
                    <p class="accordion__empty">Nessuna etichetta disponibile.</p>
                  }
                </div>
              }
            </section>
          </div>
        }
      </div>

      <!-- ─── Footer ────────────────────────────────────────────── -->
      @if (!loading && order) {
        <footer class="modal__footer">
          <button type="button" class="btn btn--ghost" (click)="close()">
            Chiudi
          </button>
        </footer>
      }
    </div>
  </div>
}
